'use client';

import { type RefObject, useEffectEvent, useLayoutEffect, useRef } from 'react';

import { cn } from '@ci/ui/utils';

import {
  type CardPose,
  type CardRect,
  CELEBRATION_DURATION_S,
  buildCelebrationScene,
  celebrationCardPose,
  isCelebrationCardSettled,
  paintScene,
  resizeScene,
} from '~/lib/release-celebrations/celebration-scene';
import type { CelebrationPlan } from '~/lib/release-celebrations/celebration-variety';
import { DRAWS_OVER_CARD } from '~/lib/release-celebrations/construction-scene';

/**
 * The card's resting box. Offsets ignore transforms, so this is where the
 * card rests even while a scene is carrying it. Its offset parent is the
 * notice's fixed, full-viewport layer, so these are viewport coordinates.
 */
function measureCard(card: HTMLElement | null): CardRect | undefined {
  if (!card) return undefined;
  return {
    left: card.offsetLeft,
    top: card.offsetTop,
    width: card.offsetWidth,
    height: card.offsetHeight,
  };
}

/**
 * Puts the card where the scene says. Until it has settled it is also inert:
 * it cannot be clicked or tabbed into while it is hidden or being carried in.
 */
function applyPose(card: HTMLElement, pose: CardPose, settled: boolean) {
  if (settled) {
    card.style.removeProperty('transform');
    card.style.removeProperty('opacity');
    card.removeAttribute('inert');
    return;
  }
  card.style.transform = `translate(${pose.dx}px, ${pose.dy}px) rotate(${pose.rotation}rad) scale(${pose.scale})`;
  card.style.opacity = String(pose.opacity);
  card.setAttribute('inert', '');
}

/**
 * PAY-4392: plays one {@link CelebrationPlan} across the whole viewport.
 *
 * Canvas, not DOM: cranes and wrecking balls do not fit a few keyframes.
 * Decorative only: `aria-hidden`, `pointer-events: none`, silent.
 * It removes itself (`onDone`) when the style has played. The notice sits
 * above this layer, except for the few styles that draw over it
 * (`DRAWS_OVER_CARD`). Where the browser cannot draw, the canvas stays up for
 * the same length and then leaves, so a missing context never traps it.
 *
 * PAY-4631: every style also moves the notice (`cardRef`) from the same
 * frame loop, so a crane's slings and the card it carries never
 * drift apart. A layout effect, so the card is already in its opening pose
 * on the first paint. Whenever the scene ends or is torn down early, the
 * card is put back at rest; and where nothing can be drawn it is never moved.
 */
export function CelebrationBurst({
  plan,
  cardRef,
  onDone,
}: {
  plan: CelebrationPlan;
  cardRef?: RefObject<HTMLElement | null>;
  onDone: () => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const done = useEffectEvent(onDone);

  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    let ended = false;
    const finish = () => {
      if (ended) return;
      ended = true;
      done();
    };
    const backup = window.setTimeout(
      finish,
      CELEBRATION_DURATION_S * 1000 + 50,
    );

    const context = canvas?.getContext('2d');
    if (!canvas || !context) {
      return () => window.clearTimeout(backup);
    }

    const card = cardRef?.current ?? null;
    const scene = buildCelebrationScene({
      seed: plan.seed,
      style: plan.style,
      palette: plan.paletteName,
      width: window.innerWidth,
      height: window.innerHeight,
      card: measureCard(card),
      dark: document.documentElement.classList.contains('dark'),
    });
    const pose = (time: number) => {
      if (!card) return;
      applyPose(
        card,
        celebrationCardPose(scene, time),
        isCelebrationCardSettled(scene, time),
      );
    };

    const fitCanvas = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(scene.width * dpr);
      canvas.height = Math.round(scene.height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    const relayout = () => {
      resizeScene(scene, {
        width: window.innerWidth,
        height: window.innerHeight,
        card: measureCard(card),
      });
    };
    const onResize = () => {
      relayout();
      fitCanvas();
    };
    fitCanvas();
    pose(0);
    window.addEventListener('resize', onResize);
    // The card changes size on its own too (the pager appears once the older
    // notes load), and the props are laid out against it.
    const cardResize =
      card && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(relayout)
        : null;
    if (card) cardResize?.observe(card);

    let playing = true;
    let frame = 0;
    const startedAt = performance.now();

    const tick = (now: number) => {
      if (!playing) return;
      const time = (now - startedAt) / 1000;
      if (time > CELEBRATION_DURATION_S) {
        playing = false;
        pose(CELEBRATION_DURATION_S);
        finish();
        return;
      }
      paintScene(context, scene, time);
      pose(time);
      frame = window.requestAnimationFrame(tick);
    };
    frame = window.requestAnimationFrame(tick);

    return () => {
      playing = false;
      window.clearTimeout(backup);
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', onResize);
      cardResize?.disconnect();
      pose(CELEBRATION_DURATION_S);
    };
  }, [plan, cardRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      // Under the notice (z-50) unless the style draws over it (PAY-4644).
      className={cn(
        'pointer-events-none fixed inset-0 h-full w-full',
        DRAWS_OVER_CARD.has(plan.style) ? 'z-[60]' : 'z-40',
      )}
      data-test="update-notice-celebration"
      data-celebration-style={plan.style}
      data-celebration-palette={plan.paletteName}
    />
  );
}
